import { useMemo, useRef, useState } from 'react';
import { useNodes, useEdges } from '@xyflow/react';
import { Select } from 'antd';
import {
  findFieldItem,
  getAvailableVariablesGroup,
  variableRefName,
  type VariableListItem,
} from './variableRefs';
import { getAncestorVariables } from './variableUtils';
import type { VariableType } from '@/proto/common';
import type { ReactFlowEdge, ReactFlowNode } from '@/pages/Logiblocks/types';
import type { VariableRef } from '@/proto/logiblocks/logiblocks';
import { CustomVariablePicker } from './CustomVariablePicker';
import type { FieldItem } from '@/apis/logiblocksAPI';
import styles from './DropdownVariablePicker.module.css';
import { CustomSelectIcon } from '@/assets/icons/icons';
import { getTriggerFilterContext } from './triggerFilterUtils';

export interface DropdownVariablePickerProps {
  nodeId: string;
  value?: VariableRef;
  onChange: (variable: (VariableRef & { item: FieldItem }) | null) => void;
  disabled?: boolean;

  placeholder?: string;
  typeFilter?: VariableType[];
  whiteBackground?: boolean;
  /** Optional filter function to filter available variable items */
  itemsFilter?: (items: VariableListItem[]) => VariableListItem[];
  /** Whether to apply trigger-based field filtering (default: true) */
  applyTriggerFiltering?: boolean;
}

export function DropdownVariablePicker({
  nodeId,
  value,
  onChange,
  disabled = false,
  placeholder = 'Select variable...',
  typeFilter,
  whiteBackground = false,
  applyTriggerFiltering = true,
}: DropdownVariablePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const nodes = useNodes<ReactFlowNode>();
  const edges = useEdges<ReactFlowEdge>();

  const ancestorVariables = useMemo(
    () => getAncestorVariables(nodeId, nodes, edges),
    [nodeId, nodes, edges]
  );

  // Get trigger node and extract filtering context
  const triggerFilterContext = useMemo(() => {
    if (!applyTriggerFiltering) return null;

    const triggerNode = nodes.find((n) => {
      const nodeData = n.data;
      return !!(
        nodeData.alert_trigger ||
        nodeData.location_trigger ||
        nodeData.eta_change_trigger ||
        nodeData.status_progress_trigger ||
        nodeData.inbound_event_trigger ||
        nodeData.inbound_trigger ||
        nodeData.manual_execution_trigger ||
        nodeData.schedule_trigger_v2 ||
        nodeData.inbound_call_trigger
      );
    });

    if (!triggerNode) return null;

    return getTriggerFilterContext(triggerNode);
  }, [nodes, applyTriggerFiltering]);

  const items = useMemo(() => {
    const availableItems = getAvailableVariablesGroup(
      ancestorVariables,
      typeFilter,
      triggerFilterContext
    );
    return availableItems;
  }, [ancestorVariables, typeFilter, triggerFilterContext]);

  const selectedLabel = useMemo(() => {
    if (!value) return null;
    if (items.length > 0) {
      const field = findFieldItem(value, items);
      const group = items.find((g) => g.items.includes(field!));
      if (field && group) {
        return `${group.group_name}.${field.display_name}`;
      }
      return variableRefName(value);
    }
  }, [value, items]);

  const displayLabel = selectedLabel;

  const handleSelect = (
    nodeIdVal: string,
    variableName: string,
    item: FieldItem
  ) => {
    onChange({
      node_id: nodeIdVal,
      variable_name: variableName,
      derived_variable_name: item.derived_field_name ?? '',
      source: item.source,
      item: item,
    });
    setIsOpen(false);
  };

  const dropdownContent = (
    <div className={styles.dropdownPanel}>
      <CustomVariablePicker
        groups={items}
        onSelect={handleSelect}
        onDismiss={() => setIsOpen(false)}
        autoFocus={isOpen}
      />
    </div>
  );

  return (
    <div ref={triggerRef}>
      <Select
        placeholder={placeholder}
        value={displayLabel}
        open={isOpen && !disabled}
        onOpenChange={(open) => !disabled && setIsOpen(open)}
        disabled={disabled}
        popupRender={() => dropdownContent}
        popupMatchSelectWidth={true}
        className='lb-select'
        style={{
          width: '100%',
          ...(whiteBackground
            ? { backgroundColor: 'var(--surface-container)' }
            : {}),
        }}
        styles={{
          popup: {
            root: {
              padding: 0,
            },
          },
          ...(whiteBackground && {
            selector: { backgroundColor: 'var(--surface-container)' },
          }),
        }}
        suffixIcon={<CustomSelectIcon />}
        labelRender={() =>
          displayLabel ? (
            <span className={styles.selectedTag}>
              <span className={styles.selectedTagLabel}>{displayLabel}</span>
              <span
                className={styles.selectedTagClear}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange(null);
                }}
              >
                ✕
              </span>
            </span>
          ) : (
            <span className={styles.placeholder}>Select...</span>
          )
        }
      ></Select>
    </div>
  );
}
